import * as React from 'react';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { FormControl, Typography } from '@mui/material';
import Box from '@mui/material/Box';

// Define your languages configuration
const LANGUAGES = [
  {
    code: 'en',
    displayCode: 'EN',
    flagIcon: () => import('@assets/UKFlag').then(mod => mod.default),
    isDefault: true
  },
  {
    code: 'cz',
    displayCode: 'CZ',
    flagIcon: () => import('@assets/CzechFlag').then(mod => mod.default),
    isDefault: false
  },
  // Add more languages as needed
  // {
  //   code: 'de',
  //   displayCode: 'DE',
  //   flagIcon: () => import('@assets/GermanFlag').then(mod => mod.default),
  //   isDefault: false
  // }
];

// Component for displaying flag + language code
function FlagLanguageCombo({ languageCode, FlagIcon }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1
      }}
    >
      <FlagIcon />
      <Typography
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {languageCode}
      </Typography>
    </Box>
  );
}

export default function LanguageSelect(props) {
  const { i18n } = useTranslation();
  const [flagIcons, setFlagIcons] = React.useState({});

  // Load flag icons dynamically
  React.useEffect(() => {
    const loadIcons = async () => {
      const icons = {};
      for (const lang of LANGUAGES) {
        icons[lang.code] = await lang.flagIcon();
      }
      setFlagIcons(icons);
    };
    loadIcons();
  }, []);

  // Set default language
  React.useEffect(() => {
    if (!i18n.language || !LANGUAGES.some(lang => lang.code === i18n.language)) {
      const defaultLang = LANGUAGES.find(lang => lang.isDefault) || LANGUAGES[0];
      i18n.changeLanguage(defaultLang.code);
    }
  }, [i18n]);

  const handleChange = (event) => {
    i18n.changeLanguage(event.target.value);
  };

  const renderValue = (value) => {
    const language = LANGUAGES.find(lang => lang.code === value) || 
                    LANGUAGES.find(lang => lang.isDefault) || 
                    LANGUAGES[0];
    
    const FlagIcon = flagIcons[language.code];
    if (!FlagIcon) return language.displayCode;

    return (
      <FlagLanguageCombo 
        languageCode={language.displayCode} 
        FlagIcon={FlagIcon} 
      />
    );
  };

  // Get current language or fallback to default
  const currentLanguage = LANGUAGES.some(lang => lang.code === i18n.language) 
    ? i18n.language 
    : (LANGUAGES.find(lang => lang.isDefault) || LANGUAGES[0]).code;

  return (
    <FormControl>
      <Select
        value={currentLanguage}
        onChange={handleChange}
        SelectDisplayProps={{
          'data-screenshot': 'toggle-language',
        }}
        autoWidth={true}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
        renderValue={renderValue}
        {...props}
      >
        {LANGUAGES.map((language) => {
          const FlagIcon = flagIcons[language.code];
          if (!FlagIcon) return null;

          return (
            <MenuItem key={language.code} value={language.code}>
              <FlagLanguageCombo 
                languageCode={language.displayCode} 
                FlagIcon={FlagIcon} 
              />
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}